import { useState, useEffect } from 'react';
import { useAssetsLogoAndFaviconResource } from '@atlassian/jira-router-resources-assets-admin-settings/src/index.tsx';
import {
	uploadFavicon as uploadFaviconService,
	resetFaviconToDefault as resetFaviconToDefaultService,
} from '@atlassian/jira-router-resources-assets-admin-settings/src/services/index.tsx';
import { emptyLogoResponse } from '@atlassian/jira-router-resources-assets-admin-settings/src/common/constants.tsx';

export const useFaviconService = () => {
	const { data: resourceData, loading, error, update } = useAssetsLogoAndFaviconResource();

	const [faviconState, setFaviconState] = useState({
		loading,
		error: error instanceof Error ? error : null,
		data: resourceData?.favicon ?? null,
	});

	useEffect(() => {
		if (!loading && resourceData) {
			setFaviconState((prev) => ({ ...prev, data: resourceData.favicon }));
		}
	}, [loading, resourceData]);

	const uploadFavicon = async (
		workspaceId: string,
		payload: { file: File; fileName: string },
	): Promise<void> => {
		setFaviconState((prev) => ({ ...prev, loading: true, error: null }));

		try {
			const uploadedFavicon = await uploadFaviconService(workspaceId, payload);

			update((currentData) => {
				return currentData
					? { ...currentData, favicon: uploadedFavicon }
					: { logo: emptyLogoResponse, favicon: uploadedFavicon };
			});

			setFaviconState({
				loading: false,
				error: null,
				data: uploadedFavicon,
			});
		} catch (uploadError) {
			setFaviconState({
				loading: false,
				error: uploadError instanceof Error ? uploadError : null,
				data: null,
			});
			throw uploadError;
		}
	};

	const resetFaviconToDefault = async (workspaceId: string): Promise<void> => {
		setFaviconState((prev) => ({ ...prev, loading: true, error: null }));

		try {
			const defaultFavicon = await resetFaviconToDefaultService(workspaceId);

			update((currentData) => {
				return currentData
					? { ...currentData, favicon: defaultFavicon }
					: { logo: emptyLogoResponse, favicon: defaultFavicon };
			});

			setFaviconState({
				loading: false,
				error: null,
				data: defaultFavicon,
			});
		} catch (resetError) {
			setFaviconState({
				loading: false,
				error: resetError instanceof Error ? resetError : null,
				data: null,
			});
			throw resetError;
		}
	};

	return {
		faviconState,
		uploadFavicon,
		resetFaviconToDefault,
		isDefaultFavicon: Boolean(resourceData?.favicon?.isDefaultFavicon),
	};
};
